import React from "react";

const ScholarshipAgreementModal = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-xl p-8 relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-xl"
                >
                    &times;
                </button>

                {/* Modal Content */}
                <div className=" text-left max-w-5xl mx-auto bg-white p-8 rounded-xl shadow-xl text-gray-800 space-y-6">
                    <h1 className="text-3xl font-bold text-center text-blue-700">
                        🎓 Scholarship Agreement & Policy
                    </h1>
                    <p className="text-center text-sm text-gray-600">
                        Backend Engineering Diploma Program
                    </p>

                    <section>
                        <p>
                            This Agreement sets forth the terms and conditions under which a student may be eligible
                            for a performance-based scholarship as part of the Backend Engineering Diploma Program
                            (hereinafter referred to as "the Program") administered by Enginuity System.
                        </p>
                        <br/>
                        <p>
                            By signing this Agreement, the student agrees to be bound by the terms outlined herein,
                            and understands that all obligations must be fulfilled to remain eligible for scholarship benefits.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold">1. Program Overview</h2>
                        <p className="text-xl mt-2">1.1 Program Duration</p>
                        <p>
                            The Program spans 12 months, divided into two semesters of 6 months each. Each semester
                            includes multiple courses/modules with structured assessments and project-based evaluations.
                        </p>
                        <p className="text-xl mt-2">1.2 Scholarship Type</p>
                        <p>
                            The scholarship is performance-based, contingent upon the student’s academic, behavioral,
                            and professional performance in each course/module throughout the Program.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold">2. Fee Structure & Scholarship Refund Policy</h2>
                        <p className="text-xl mt-2">2.1 Full Fee Requirement</p>
                        <p>
                            Students must deposit the entire course fee upfront as a security measure to initiate
                            enrollment and scholarship consideration.
                        </p>
                        <p className="text-xl mt-2">2.2 Refund Structure</p>
                        <ul className="list-disc ml-6">
                            <li>40% refund at the end of Semester 1</li>
                            <li>40% refund at the end of Semester 2</li>
                            <li>20% is non-refundable, retained as an admission and administrative fee</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold">3. Scholarship Eligibility Criteria</h2>
                        <p>
                            To remain eligible for scholarship refunds, students must meet all of the following
                            conditions for each individual course/module in every semester:
                        </p>
                        <p className="text-xl mt-2">3.1 Academic Requirements</p>
                        <ul className="list-disc ml-6">
                            <li>Achieve a minimum score of 90% in every course/module</li>
                            <li>Complete 100% of assignments, projects, and quizzes</li>
                            <li>Submit all work within the given deadlines</li>
                        </ul>
                        <p className="text-xl mt-2">3.2 Attendance & Participation</p>
                        <ul className="list-disc ml-6">
                            <li>Maintain at least 90% attendance in all scheduled live sessions</li>
                            <li>Be actively engaged in class discussions, activities, and coding sessions</li>
                        </ul>
                        <p className="text-xl mt-2">3.3 Behavior and Professionalism</p>
                        <ul className="list-disc ml-6">
                            <li>Maintain respectful and professional conduct toward instructors, mentors, and peers</li>
                            <li>Comply with all class and community guidelines</li>
                        </ul>
                        <p className="text-xl mt-2">3.4 Academic Integrity</p>
                        <ul className="list-disc ml-6">
                            <li>All submitted work must be original</li>
                            <li>Plagiarism, code copying, or dishonesty will result in immediate disqualification</li>
                        </ul>
                        <p className="text-xl mt-2">3.5 Employment Status</p>
                        <ul className="list-disc ml-6">
                            <li>The scholarship is exclusively for non-employed individuals</li>
                            <li>
                                If found to be currently employed (part-time/full-time):
                                <ul className="list-disc ml-6">
                                    <li>Scholarship will be immediately revoked</li>
                                    <li>Admission will be cancelled without refund</li>
                                </ul>
                            </li>
                        </ul>
                        <p className="text-xl mt-2">3.6 Continuous Evaluation</p>
                        <ul className="list-disc ml-6">
                            <li>Each course/module will be individually reviewed after completion</li>
                            <li>
                                Failure to meet any condition in a module disqualifies scholarship refund for that semester
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold">4. Monitoring and Evaluation Process</h2>
                        <p className="text-xl mt-2">4.1 Per-Course Evaluation</p>
                        <ul className="list-disc ml-6">
                            <li>Academic score analysis</li>
                            <li>Attendance review</li>
                            <li>Assignment/project completion status</li>
                            <li>Integrity verification (via plagiarism check)</li>
                            <li>Mentor/instructor feedback</li>
                        </ul>
                        <p className="text-xl mt-2">4.2 Semester-End Scholarship Review</p>
                        <p>
                            Final refund decisions are made based on aggregated module performance for the semester.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold">5. Disqualification Conditions</h2>
                        <ul className="list-disc ml-6">
                            <li>Failure to achieve 90% marks in any course/module</li>
                            <li>Plagiarism or dishonest academic behavior</li>
                            <li>Disruptive or disrespectful behavior</li>
                            <li>Incomplete submission of tasks or assessments</li>
                            <li>Verified employment during the Program</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-xl font-semibold">6. One-Month Probation Period</h2>
                        <p>To qualify for scholarship tracking, students will undergo a 1-month observation period with the following rules:</p>

                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-left border border-gray-300">
                                <thead className="bg-gray-100 text-gray-700">
                                    <tr>
                                        <th className="px-4 py-2 border">Criteria</th>
                                        <th className="px-4 py-2 border">Rules & Tracking</th>
                                        <th className="px-4 py-2 border">Evaluation Method</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="px-4 py-2 border">Dedication</td>
                                        <td className="px-4 py-2 border">Attend at least 90% of live classes/sessions, submit weekly reports.</td>
                                        <td className="px-4 py-2 border">Attendance, submission logs</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 border">Responsibility</td>
                                        <td className="px-4 py-2 border">Meet deadlines, complete tasks without reminders.</td>
                                        <td className="px-4 py-2 border">Deadline tracking, mentor reviews</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 border">Commitment</td>
                                        <td className="px-4 py-2 border">Minimum 2-3 hours daily coding/study time.</td>
                                        <td className="px-4 py-2 border">Learning log / GitHub commits</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 border">Respect for Rules</td>
                                        <td className="px-4 py-2 border">Follow community guidelines, avoid disruptions.</td>
                                        <td className="px-4 py-2 border">Admin reports</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 border">Communication</td>
                                        <td className="px-4 py-2 border">Respond within 24 hrs to official comms.</td>
                                        <td className="px-4 py-2 border">Email/Slack log</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 border">Growth Mindset</td>
                                        <td className="px-4 py-2 border">Provide feedback, show weekly improvement.</td>
                                        <td className="px-4 py-2 border">1:1s, mentor notes</td>
                                    </tr>
                                    <tr>
                                        <td className="px-4 py-2 border">Integrity</td>
                                        <td className="px-4 py-2 border">No plagiarism or dishonesty in work.</td>
                                        <td className="px-4 py-2 border">Code similarity check</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h3 className="text-lg font-semibold mt-4">📜 Code of Conduct</h3>
                        <ul className="list-disc ml-6 space-y-2">
                            <li>Academic Honesty: No plagiarism.</li>
                            <li>Respect all instructors and peers.</li>
                            <li>3 late joins = 1 absence.</li>
                            <li>Log progress weekly.</li>
                            <li>No dual program enrollment without approval.</li>
                            <li>Misuse of scholarship = permanent disqualification.</li>
                        </ul>

                        <h3 className="text-lg font-semibold mt-4">🔍 Evaluation at End of Month 1</h3>
                        <ul className="list-disc ml-6 space-y-2">
                            <li>✅ Approved for Scholarship Tracking</li>
                            <li>⚠️ Warning / 1-Week Extension</li>
                            <li>❌ Rejected (continue as regular student)</li>
                        </ul>
                       
                    </section>
                   
                </div>
                
            </div>
        </div>
    );
};

export default ScholarshipAgreementModal;
